/**
 * ч.744–745 (рішення власника): податкові рахунки організації — одна таблиця
 * `.tch` на картці (вкладка «Податкові рахунки») і на кроці 3 майстра.
 * Три системні рядки (ПДФО / ВЗ / ЄСВ з ЗП — колонки організації, їх читає
 * «Перерахування податків ЗП») + користувацькі рядки `tax_accounts_extra`
 * (єдиний податок, ВЗ до ЄП, податок на прибуток, ПДВ, земельний…).
 * Колонки: Податок | Отримувач | Код отримувача | IBAN. МФО не вводиться —
 * з IBAN; назва банку — у підказці поля IBAN. Без підказок і рамок.
 */
import type { OrganizationCreate, OrgTaxAccount } from '@/api/catalogs';
import { findBankByIban, isValidUaIban, type BankRow } from '@/shared/ukr_classifiers';
import { DEFAULT_TAX_PURPOSE_TEMPLATE, TAX_PURPOSE_VARS_HINT } from '@/shared/taxPurposeTemplate';
import { signalUserEdit } from '@/store/formDrafts';
import TchTable from '@/shared/components/TchTable';

const SYSTEM_ROWS = [
  { key: 'pdfo', label: 'ПДФО (ЗП)' },
  { key: 'wz', label: 'Військовий збір (ЗП)' },
  { key: 'esv', label: 'ЄСВ (ЗП)' },
] as const;

// Підказки назв для нових рядків (datalist) — вільний ввід теж дозволено.
export const TAX_NAME_SUGGESTIONS = [
  'Єдиний податок',
  'Військовий збір (єдиний податок)',
  'Податок на прибуток',
  'ПДВ',
  'Земельний податок',
  'Орендна плата за землю',
  'Податок на нерухомість',
  'Екологічний податок',
  'Акцизний податок',
  'ЄСВ (за себе, ФОП)',
];

const cleanIban = (v: string) => v.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 34);

export default function OrgTaxAccountsTable(props: {
  form: OrganizationCreate;
  setForm: (f: OrganizationCreate) => void;
  banks: BankRow[];
  style?: React.CSSProperties;
}) {
  const { form, setForm, banks, style } = props;
  const extra: OrgTaxAccount[] = form.tax_accounts_extra ?? [];
  // ч.1059: додати/прибрати рядок — правка користувача (чернетка картки).
  const setExtra = (rows: OrgTaxAccount[]) => {
    signalUserEdit();
    setForm({ ...form, tax_accounts_extra: rows });
  };
  const patchExtra = (i: number, patch: Partial<OrgTaxAccount>) =>
    setExtra(extra.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const ibanCell = (value: string, onChange: (v: string) => void) => {
    const bank = findBankByIban(value, banks);
    return (
      <input value={value} maxLength={34} title={bank ?? undefined}
        style={value && !isValidUaIban(value) ? { borderColor: '#d33' } : undefined}
        onChange={(e) => onChange(cleanIban(e.target.value))} />
    );
  };

  return (
    <div style={style}>
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 150 }}>Податок</th>
            <th style={{ width: 200 }}>Отримувач</th>
            <th style={{ width: 110 }}>Код отримувача</th>
            <th style={{ width: 250 }}>IBAN</th>
            <th title={TAX_PURPOSE_VARS_HINT}>Призначення платежу (шаблон)</th>
            <th style={{ width: 36 }}></th>
          </tr>
        </thead>
        <tbody>
          {SYSTEM_ROWS.map(({ key, label }) => {
            const nameField = `tax_${key}_recipient_name` as const;
            const edrpouField = `tax_${key}_recipient_edrpou` as const;
            const ibanField = `tax_${key}_recipient_iban` as const;
            return (
              <tr key={key}>
                <td style={{ verticalAlign: 'middle' }}>{label}</td>
                <td>
                  <input value={form[nameField] ?? ''}
                    onChange={(e) => setForm({ ...form, [nameField]: e.target.value || null })} />
                </td>
                <td>
                  <input value={form[edrpouField] ?? ''} maxLength={10}
                    onChange={(e) => setForm({ ...form,
                      [edrpouField]: e.target.value.replace(/\D/g, '') || null })} />
                </td>
                <td>{ibanCell(form[ibanField] ?? '', (v) => setForm({ ...form, [ibanField]: v || null }))}</td>
                <td>
                  <input value={form.tax_purpose_templates?.[key] ?? ''} maxLength={420}
                    placeholder={DEFAULT_TAX_PURPOSE_TEMPLATE} title={TAX_PURPOSE_VARS_HINT}
                    onChange={(e) => setForm({ ...form, tax_purpose_templates: {
                      ...(form.tax_purpose_templates ?? {}), [key]: e.target.value } })} />
                </td>
                <td />
              </tr>
            );
          })}
          {extra.map((row, i) => (
            <tr key={`x-${i}`}>
              <td>
                <input value={row.name} list="org-tax-name-suggestions" placeholder="назва податку"
                  onChange={(e) => patchExtra(i, { name: e.target.value })} />
              </td>
              <td>
                <input value={row.recipient_name ?? ''}
                  onChange={(e) => patchExtra(i, { recipient_name: e.target.value || null })} />
              </td>
              <td>
                <input value={row.recipient_edrpou ?? ''} maxLength={10}
                  onChange={(e) => patchExtra(i, { recipient_edrpou: e.target.value.replace(/\D/g, '') || null })} />
              </td>
              <td>{ibanCell(row.recipient_iban ?? '', (v) => patchExtra(i, { recipient_iban: v || null }))}</td>
              <td>
                <input value={row.purpose_template ?? ''} maxLength={420}
                  placeholder={DEFAULT_TAX_PURPOSE_TEMPLATE} title={TAX_PURPOSE_VARS_HINT}
                  onChange={(e) => patchExtra(i, { purpose_template: e.target.value || null })} />
              </td>
              <td style={{ textAlign: 'right' }}>
                <button type="button" className="danger" title="Видалити рядок"
                  onClick={() => setExtra(extra.filter((_, j) => j !== i))}>×</button>
              </td>
            </tr>
          ))}
        </tbody>
      </TchTable>
      <datalist id="org-tax-name-suggestions">
        {TAX_NAME_SUGGESTIONS.map((n) => <option key={n} value={n} />)}
      </datalist>
      <button type="button" style={{ marginTop: '0.5rem' }}
        onClick={() => setExtra([...extra, { name: '', recipient_name: null, recipient_edrpou: null, recipient_iban: null, purpose_template: null }])}>
        додати рядок
      </button>
    </div>
  );
}
